/* @flow */

/* 
 * This module is responsible for sharing your map preview images.
 */

import { mapDimensions, ranks } from './constants';
import Tooltip from './tooltip';
import { stateFactoids } from './help';
const pubSub = require('./pubsub');

/*::
import d3 from 'javascriptLibs'
*/

let page = ((document.querySelector('#page'): any): HTMLElement);
let container = ((document.querySelector('#map-container'): any): HTMLElement);

interface Description {
    title: string;
    summary: string;
}

const tooltip = new Tooltip();

// helper method for scaling projection to best fit within bounding box
const scaleProjection = (projection) => {
    return {
        scale: projection.scale(),
        offset: projection.translate()
    }
}

const defaultWidth = 960,   // dimensions of default projection (Albers)
      defaultHeight = 500,
      defaultProjection = d3.geoAlbersUsa(),
      defaultScaleObj = scaleProjection(defaultProjection),
      previewHeight = 650,  // dimensions of preview canvas
      previewWidth = 1000,
      mapScale = 1.2,       // map scale allows us to change map size in relation to preview canvas
      origScale = Math.min(mapDimensions.width/defaultWidth, mapDimensions.height/defaultHeight),
      origOffsetX = (origScale - 1) * defaultWidth/2,
      origOffsetY = (origScale - 1) * defaultHeight/2,
      sourceCanvas = ((document.getElementById('canvas'): any): HTMLCanvasElement),
      summaryOffsetY = 30,
      legendX = previewWidth - 75,
      legendY = previewHeight - 170,
      boxHeight = 15,
      logo = new Image(),
      logoScale = 0.4,
      logoX = previewWidth - 240,
      logoY = 15,
      origProjection = d3.geoAlbersUsa()
        .scale(origScale * defaultScaleObj.scale)
        .translate([defaultScaleObj.offset[0] + origOffsetX, defaultScaleObj.offset[1] + origOffsetY]),
      previewScale = Math.min(previewWidth/defaultWidth, previewHeight/defaultHeight),
      previewOffsetX = (previewScale - 1) * defaultWidth/2,
      previewOffsetY = (previewScale - 1) * defaultHeight/2,
      projection = d3.geoAlbersUsa()
        .scale(previewScale * defaultScaleObj.scale)
        .translate([defaultScaleObj.offset[0] + previewOffsetX, defaultScaleObj.offset[1] + previewOffsetY]),
      canvas = ((document.querySelector('#preview-generator'): any): HTMLCanvasElement),
      ctx = canvas.getContext('2d'),
      tooltipCanvas = ((document.querySelector('#preview-generator'): any): HTMLCanvasElement),
      tooltipCtx = tooltipCanvas.getContext('2d'),
      previewDialog = ((document.querySelector('#share-map-image'): any): HTMLDialogElement),
      previewDialogImg = ((document.querySelector('#preview-image'): any): HTMLImageElement),
      previewDialogUrl = ((document.querySelector('#preview-image-url'): any): HTMLElement),
      errorMessage = ((document.querySelector('#error-msg'): any): HTMLElement);

logo.src = "/static/logo_long.svg";

// tooltip manipulation
const selectedLocations = {};
const previewTooltipArea = ((document.querySelector('#preview-tooltips'): any): HTMLElement);
const locationName = ((document.querySelector('#preview-search-field'): any): HTMLInputElement);
const addLocation = ((document.querySelector('#add-tooltip'): any): HTMLElement);
const categoryLegend = ((document.querySelector('#category-legend'): any): HTMLElement);
const categoryLegendStats = ((document.querySelector('#category-legend-stats'): any): HTMLElement);

((document.querySelector('#share-img-btn'): any): HTMLElement).onclick = () => {
    let title = 'United States'
    let url = new URL(window.location.href);
    let stateCode
    if (stateCode = url.searchParams.get('focus')) {
        title = stateFactoids[stateCode].title
    }
    const year = parseInt(document.querySelector('#timeline-year').innerText);
    title += `, ${year}`

    const summaryElement = document.querySelector('#timeline-stats');
    let summary = summaryElement.innerText
    let currentYear = new Date().getFullYear()
    if (!summary) {
        title = ''
        summary = ''
    } else if (year > currentYear) {
        summary += ' (Prediction)'
    } else if (year > currentYear - 2 || year < 2000) {
        summary += ' (Estimate)'
    }

    generatePreview({
        title,
        summary
    })
    previewTooltipArea.innerText = ''
};

// select url text on click
previewDialogUrl.onclick = () => {
    window.getSelection().selectAllChildren(previewDialogUrl);
}

// track current zoom level
let zoomedInTopojson = null;
let zoomedInGeojson = null;
pubSub.subscribe('zoom:enhance', (topojson) => {
    zoomedInTopojson = topojson.topo;
    zoomedInGeojson = topojson.geo;
})
let zoomedInLoc = null;
pubSub.subscribe('location:load', (loc) => {
    zoomedInLoc = loc;
})
pubSub.subscribe('zoom:reset', () => {
    zoomedInTopojson = null;
    zoomedInGeojson = null;
    zoomedInLoc = null;
})

let mainFont = 'Lato'

// Logic used for generating image previews
let transform = d3.geoAlbersUsa();
function generatePreview(desc: Description): void {
    let infoLocation = 'left'
    canvas.width = previewWidth;
    canvas.height = previewHeight;

    let origScaleObj = scaleProjection(origProjection);
    let newScale = Math.min(previewWidth/defaultWidth, previewHeight/defaultHeight) * (defaultScaleObj.scale / origScaleObj.scale) * mapScale;
    let offsetX = Math.max(-100, (previewWidth / 2) - (mapDimensions.width / 2) * newScale);
    let offsetY = Math.max(0, (previewHeight / 2) - (mapDimensions.height / 2) * newScale); // Math.max prevents map from rendering off-screen
    console.log(defaultScaleObj, origScaleObj, newScale, [offsetX, offsetY])

    // white background
    ctx.save()
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, previewWidth, previewHeight);
    ctx.restore()

    // render map
    const urlQuery = new URLSearchParams(location.search);
    let infographic = [];
    if (zoomedInGeojson) {

        ctx.save()
        ctx.lineWidth = 0.5;
        ctx.strokeStyle = '#999';
        let canvasPath = d3.geoPath().context(ctx).projection(d3.geoAlbersUsa());
        let bounds = canvasPath.bounds(zoomedInGeojson),
            dx = bounds[1][0] - bounds[0][0],
            dy = bounds[1][1] - bounds[0][1],
            x = bounds[0][0], y = bounds[0][1],
            scale = 0.90 / Math.max(dx / previewWidth, dy / previewHeight),
            translate = [dx / 2 - scale * x, dy / 2 - scale * y];
        transform = d3.geoAlbersUsa().scale(defaultScaleObj.scale * scale).translate([
            (defaultScaleObj.offset[0] - x - dx/2) * scale + previewWidth/2,
            (defaultScaleObj.offset[1] - y - dy/2) * scale + previewHeight/2
        ]);
        canvasPath = canvasPath.projection(transform);
        // let canvasPath = d3.geoPath().context(ctx).projection(transform);
        d3.select('#zipcodes_highres').selectAll('path').each(function (d) {
            ctx.beginPath();
            let fill = d3.select(this).style('fill');
            canvasPath(d);
            if (selectedLocations[d.properties.ZCTA5CE10]) {
                infographic.push({
                    zip: d.properties.ZCTA5CE10,
                    path: () => canvasPath(d)
                })
            }
            if (fill) {
                ctx.fillStyle = fill;
                ctx.fill();
            }
            ctx.stroke();
        });
        ctx.restore()
    } else {
        // render US maplegend
        ctx.drawImage(sourceCanvas, offsetX, offsetY, mapDimensions.width * newScale, mapDimensions.height * newScale);
    }

    // render legend
    const textFudgeFactor = -3;
    if (categoryLegend.offsetParent) {
        // category legend is visible, render that
        ctx.font = `12px ${mainFont}`;
        let yStart = previewHeight - categoryLegendStats.children.length * boxHeight
        Array.from(categoryLegendStats.children).forEach((catEl, idx) => {
            let vp = 2 // vertical padding
            let label = catEl.innerText || ''
            ctx.save();
            ctx.strokeStyle = 'white'
            ctx.fillStyle = catEl.firstElementChild.style.backgroundColor
            ctx.fillRect(previewWidth - boxHeight - 2 * vp, yStart + idx * boxHeight + vp, boxHeight, boxHeight - 2 * vp);
            ctx.strokeRect(previewWidth - boxHeight - 2 * vp, yStart + idx * boxHeight + vp, boxHeight, boxHeight - 2 * vp);
            ctx.restore();
            let textOffset = previewWidth - boxHeight * 1.5 - ctx.measureText(label).width - 2 * vp
            ctx.fillText(label, textOffset,  yStart + (idx + 1) * boxHeight + textFudgeFactor);
        })
    } else {
        // no category legend, render ranks
        ctx.font = `bold 12px ${mainFont}`;
        ctx.fillText('Percentile', legendX,  legendY + textFudgeFactor);
        ctx.font = `12px ${mainFont}`;
        ranks.forEach((rank, idx) => {
            const rankNumber = ranks.length - idx - 1;
            ctx.save();
            ctx.fillStyle = rank;
            ctx.fillRect(legendX, legendY + rankNumber * boxHeight, boxHeight, boxHeight);
            ctx.restore();
            ctx.fillText((rankNumber || '') + '0', legendX + boxHeight * 1.5,  legendY + (idx + 1) * boxHeight + textFudgeFactor);
        })
        ctx.fillText('No Data', legendX + boxHeight * 1.5,  legendY + (ranks.length + 1) * boxHeight + textFudgeFactor);
    }

    // draw watermark
    ctx.drawImage(logo, logoX, logoY, logo.width * logoScale, logo.height * logoScale);

    // render description
    ctx.font = `20px ${mainFont}`;
    ctx.fillText(desc.title, 10, previewHeight - summaryOffsetY)
    ctx.font = `14px ${mainFont}`;
    ctx.fillText(desc.summary, 10, previewHeight - summaryOffsetY + 20)

    // border
    ctx.strokeRect(0, 0, previewWidth, previewHeight);

    // save to server
    canvas.toBlob((blob) => {
        const formData = new FormData();
        formData.append('previewImage', blob);
        fetch(`/rank/image${location.search}`, {
            method: 'POST',
            body: formData
        })
    })

    infographic.forEach((item, idx) => {
        generateTooltip(item)
    })

    // render preview dialog
    previewDialogImg.src = canvas.toDataURL();
    previewDialogUrl.innerText = `${location.origin}/rank/image${location.search}`;
    locationName.value = '';
    previewDialog.showModal();
    page.classList.add('blur')
}

// logic to render infographic/tooltips on map
// location to include: coords or path on map to highlight, tooltip data to show
const gap = 2
let xy = { x: 0, y: 0 }
let fontDiv = ((document.querySelector('#text-measure'): any): HTMLElement);
function renderTooltip(loc: Object, tooltip: Object, align: { x: number, y: number } = xy, options: Object = {}) {
    console.log('showing tooltip', loc, tooltip, align)
    const padding = 5
    const valign = 15
    const lineHeight = 20
    ctx.save()
    if (loc.paths) {
        ctx.strokeStyle = 'blue'
        ctx.lineWidth = 2
        ctx.beginPath();
        let path = topojson.merge(zoomedInTopojson, zoomedInTopojson.objects.zips.geometries.filter((d) => {
            return loc.zips.includes(parseInt(d.properties.ZCTA5CE10))
        }))
        d3.geoPath().projection(transform).context(tooltipCtx)(path)
        // loc.paths.forEach(path =>  {
        //     d3.geoPath().projection(transform).context(tooltipCtx)(path)
        // })
        ctx.stroke()
    }
    let fields = Object.entries(tooltip.curateData(tooltip.fields))
    fontDiv.innerText = tooltip.title;
    let width = fontDiv.clientWidth;
    fields.forEach(([key, val]) => {
        fontDiv.innerText = `${key}: ${val}`;
        width = Math.max(width, fontDiv.clientWidth);
    })
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
    ctx.fillRect(align.x, align.y, width + padding * 2, (fields.length + 1) * lineHeight + padding * 2)
    ctx.fillStyle = 'white'
    ctx.font = 'bold 12pt sans-serif'
    ctx.fillText(tooltip.title, align.x + padding, align.y + padding + valign)
    ctx.font = '12pt sans-serif'
    ctx.fillStyle = '#ccc'
    fields.forEach(([key, val], i) => {
        ctx.fillText(`${key}: ${val}`, align.x + padding, align.y + padding + valign + lineHeight * (i + 1))
    })
    ctx.restore()

    // save bounds for better alginment
    let tooltipBounds = { x: align.x, y: align.y, width: mapDimensions.width + padding * 2, height: (fields.length + 1) * lineHeight + padding * 2 }
    xy.y += tooltipBounds.height + gap;
}

// logic for fetching tooltip data (also calls renderer)
function generateTooltip(item) {
    const paths = []
    if (item.zip) {
        fetch(`/tooltip/zip/${item.zip}${location.search}`).then(r => r.json()).then(json => {
            d3.select('#' + CSS.escape(`${item.zip}_HR`)).each(d => { // this runs only once, `each` is just convenient way to access the feature
                paths.push(d)
            })
            renderTooltip({
                paths,
            }, { title: `${json.city} (${item.zip})`, fields: json })

            // re-render image
            previewDialogImg.src = canvas.toDataURL();
        })
    } else if (item.city) {
        let city = `${item.city}, ${item.state}`
        fetch(`/msa_zipcodes/for_city/${city}`).then(r => r.json()).then(json => {
            const paths = []
            const zips = []
            Object.keys(json).forEach(state => {
                json[state].forEach(zip => {
                    zips.push(zip)
                    d3.select('#' + CSS.escape(`${(zip + '').padStart(5, '0')}_HR`)).each(d => { // this runs only once, `each` is just convenient way to access the feature
                        paths.push(d)
                    })
                })
            })
            renderTooltip({
                paths,
                zips,
            }, { title: city, fields: json })

            // re-render image
            previewDialogImg.src = canvas.toDataURL();
        })
    }
}

// convenience function that makes sure the tooltip is always completely on-screen, if possible
function alignTooltip(canvas, label) {
    let newX = 0, newY = 0;
    if (label.x < canvas.x) {
        newX = label.x;
    } else if (label.x + label.width > canvas.x + canvas.width) {
        newX = canvas.x + canvas.width - label.width;
    }
    if (label.y < canvas.y) {
        newY = label.y;
    } else if (label.y + label.height > canvas.y + canvas.height) {
        newY = canvas.y + canvas.height - label.height;
    }
    return { x: newX, y: newY };
}

// marks location on the map, or renders error if location is out of bounds
function markLocation(type: string, query) {
    if (zoomedInLoc && type === 'state') {
        errorMessage.innerText = `${query.query} state is outside the bounds of current map.`
    } else {
        // TODO: add safety checks to make sure we're in correct state/city to render marker/zipcode/city

        errorMessage.innerText = ''
        let tagName = '?';
        if (type === 'zipcode') {
            selectedLocations[query] = 1;
            generateTooltip({
                zip: query,
            });
            tagName = query ;
        } else if (type === 'city') {
            let city = `${query.city}, ${query.state}`
            selectedLocations[city] = 1;
            generateTooltip({
                city: query.city,
                state: query.state
            })
            tagName = city;
        }
        previewTooltipArea.innerHTML = previewTooltipArea.innerHTML + `
            <span class="mdl-chip mdl-chip--deletable">
                <span class="mdl-chip__text">${tagName}</span>
                <button type="button" class="mdl-chip__action"><i class="material-icons">cancel</i></button>
            </span>
        `;
        locationName.value = '';
    }
}

// event handlers (events themselves are fired by search module)
pubSub.subscribe('preview:marker', (r) => {
    console.log('marker', r)
})
pubSub.subscribe('preview:geocode', (r) => {
    console.log('geocode', r)
})
pubSub.subscribe('preview:zipcode', (r) => {
    console.log('zipcode', r)
    markLocation('zipcode', r)
})
pubSub.subscribe('preview:city', (r) => {
    console.log('city', r)
    markLocation('city', r)
})
pubSub.subscribe('preview:state', (r) => {
    console.log('state', r)
})
